import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FileText, Image as ImageIcon, File, Loader2 } from 'lucide-react';
import { Document } from '@/types/document';
import { formatFileSize } from '@/utils/formatters';
import { useDocumentBlob } from '@/hooks/react-query/useDocuments';

interface DocumentPreviewProps {
  document: Document;
  children: React.ReactNode;
  delay?: number;
}

export function DocumentPreview({
  document,
  children,
  delay = 500,
}: DocumentPreviewProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadPreview, setShouldLoadPreview] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Only load preview for images and PDFs
  const mimeType = document.mime_type.toLowerCase();
  const canPreview =
    mimeType.startsWith('image/') || mimeType === 'application/pdf';

  // React Query hook
  const {
    data: documentBlob,
    isLoading: loading,
    error: loadError,
  } = useDocumentBlob(document.id, {
    enabled: shouldLoadPreview && canPreview,
  });

  // Derive document URL from blob during render (no state needed)
  const documentUrl = useMemo(() => {
    if (!documentBlob) return null;
    return URL.createObjectURL(documentBlob);
  }, [documentBlob]);

  // Cleanup object URL when it changes or on unmount
  useEffect(() => {
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setTooltipPosition({
          left: Math.max(8, rect.left),
          top: rect.bottom + 8,
        });
      }
      setIsVisible(true);
      setShouldLoadPreview(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    // Keep the preview loaded to avoid reloading on next hover
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex items-center justify-center p-4 text-gray-500">
          <FileText className="h-6 w-6" />
          <span className="ml-2 text-sm">Preview unavailable</span>
        </div>
      );
    }

    const mimeType = document.mime_type.toLowerCase();

    if (mimeType.startsWith('image/') && documentUrl) {
      return (
        <div className="relative">
          <img
            src={documentUrl}
            alt={document.original_filename}
            className="max-w-full max-h-48 object-contain rounded"
            onError={() => console.error('Image failed to load')}
          />
        </div>
      );
    }

    if (mimeType === 'application/pdf' && documentUrl) {
      return (
        <div className="relative">
          <iframe
            src={`${documentUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full h-48 border-0 rounded"
            title={`Preview of ${document.original_filename}`}
          />
        </div>
      );
    }

    // Fallback for other file types
    return (
      <div className="flex items-center justify-center p-4 text-gray-500">
        <File className="h-6 w-6" />
        <span className="ml-2 text-sm">No preview available</span>
      </div>
    );
  };

  const getPreviewIcon = () => {
    const mimeType = document.mime_type.toLowerCase();

    if (mimeType.startsWith('image/')) {
      return <ImageIcon className="h-4 w-4" />;
    }

    return <FileText className="h-4 w-4" />;
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isVisible && (
        <div
          className="fixed z-9999 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-80 max-w-96 pointer-events-none"
          style={{
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            maxWidth: `${Math.min(384, window.innerWidth - 16)}px`,
          }}
        >
          {/* Header */}
          <div className="flex items-start space-x-2 mb-3">
            <div className="shrink-0 mt-1">{getPreviewIcon()}</div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {document.original_filename}
              </h4>
              <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                <span className="bg-gray-100 px-2 py-0.5 rounded">
                  {document.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                </span>
                <span>{formatFileSize(document.file_size)}</span>
              </div>
            </div>
          </div>

          {/* Preview Content */}
          <div className="border rounded bg-gray-50">
            {renderPreviewContent()}
          </div>

          {/* Footer */}
          <div className="mt-2 text-xs text-gray-500 text-center">
            Uploaded {new Date(document.upload_date).toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
}
